import { useState } from 'react';
import { Loader2, FileSearch } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const CLASS_COLORS = {
  bros_native_pdf: 'border-green-200 dark:border-green-800',
  external_pdf: 'border-blue-200 dark:border-blue-800',
  no_pdf: 'border-red-200 dark:border-red-800',
  other: 'border-amber-200 dark:border-amber-800',
};

const CLASS_TEXT = {
  bros_native_pdf: 'text-green-600',
  external_pdf: 'text-blue-600',
  no_pdf: 'text-red-600',
  other: 'text-amber-600',
};

const CLASS_LABELS = {
  bros_native_pdf: 'BR OS native PDF',
  external_pdf: 'External PDF',
  no_pdf: 'No PDF',
  other: 'Other',
};

const STATUS_ORDER = ['paid', 'submitted', 'issued', 'overdue', 'draft', 'unknown'];

function deriveStatusColumns(statusMatrix) {
  const all = new Set();
  Object.values(statusMatrix).forEach((m) =>
    Object.keys(m).forEach((k) => all.add(k))
  );
  // Sort: known statuses first in STATUS_ORDER, then any unknown alphabetically.
  const known = STATUS_ORDER.filter((s) => all.has(s));
  const unknown = [...all].filter((s) => !STATUS_ORDER.includes(s)).sort();
  return [...known, ...unknown];
}

export function InvoicePdfProvenancePanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.invoicePdfProvenance();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const cls = result?.classification;
  const pop = result?.population;
  const completed = result?.probe_status === 'completed';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Invoice PDF Provenance</CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSearch className="h-4 w-4 mr-2" />}
            Scan
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          PDF provenance of not_in_xero invoices. Classifies where PDFs live for invoices absent from the connected Xero org.
        </p>
      </CardHeader>
      {result && (
        <CardContent className="space-y-4">
          {/* Probe status */}
          {!completed && (
            <div className="border rounded-lg p-3 border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/10">
              <p className="text-sm font-medium text-red-600">
                Probe did not complete: {result.probe_status}
              </p>
              {result.errors?.map((e, i) => (
                <p key={i} className="text-[10px] text-red-600/80 font-mono mt-1">{e}</p>
              ))}
            </div>
          )}

          {completed && (
            <>
              {/* Population summary */}
              <div className="grid grid-cols-2 gap-2">
                <div className="border rounded-lg p-2 text-center">
                  <p className="text-xs text-muted-foreground">Total unlinked</p>
                  <p className="text-lg font-semibold font-mono">{pop?.total_unlinked}</p>
                </div>
                <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                  <p className="text-xs text-muted-foreground">Not in Xero</p>
                  <p className="text-lg font-semibold font-mono text-red-600">{pop?.not_in_xero_count}</p>
                </div>
              </div>

              {/* Classification counts */}
              <div data-testid="classification-counts" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {['bros_native_pdf', 'external_pdf', 'no_pdf', 'other'].map((c) => (
                  <div key={c} className={`border rounded-lg p-2 text-center ${CLASS_COLORS[c] || ''}`}>
                    <p className="text-[10px] text-muted-foreground">{CLASS_LABELS[c]}</p>
                    <p className={`text-lg font-semibold font-mono ${CLASS_TEXT[c] || ''}`}>
                      {cls?.[c] ?? 0}
                    </p>
                    {result.classification_pct?.[c] != null && (
                      <p className="text-[10px] text-muted-foreground">{result.classification_pct[c]}%</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Partition line */}
              {cls && (() => {
                const sum = (cls.bros_native_pdf ?? 0) + (cls.external_pdf ?? 0) + (cls.no_pdf ?? 0) + (cls.other ?? 0);
                const ok = cls.partition_ok === true;
                return (
                  <p data-testid="partition-line" className={`text-[10px] font-mono ${ok ? 'text-muted-foreground' : 'text-red-600 font-medium'}`}>
                    Partition: {cls.bros_native_pdf ?? 0} + {cls.external_pdf ?? 0} + {cls.no_pdf ?? 0} + {cls.other ?? 0} = {sum} / {pop?.not_in_xero_count} not_in_xero {ok ? '(ok)' : '(MISMATCH)'}
                  </p>
                );
              })()}

              {/* Classification x Status matrix */}
              {result.status_matrix && Object.keys(result.status_matrix).length > 0 && (() => {
                const statusCols = deriveStatusColumns(result.status_matrix);
                return (
                  <div data-testid="status-matrix">
                    <p className="text-sm font-medium mb-2">Classification x BR OS status</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-1.5">Class</th>
                            {statusCols.map((s) => (
                              <th key={s} className="text-right p-1.5">{s}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {['bros_native_pdf', 'external_pdf', 'no_pdf', 'other']
                            .filter((c) => result.status_matrix[c])
                            .map((c) => (
                              <tr key={c} className="border-t">
                                <td className={`p-1.5 font-mono ${CLASS_TEXT[c] || ''}`}>{CLASS_LABELS[c]}</td>
                                {statusCols.map((s) => (
                                  <td key={s} className="p-1.5 text-right font-mono">
                                    {result.status_matrix[c]?.[s] || ''}
                                  </td>
                                ))}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Classification x Date bucket */}
              {result.date_matrix && Object.keys(result.date_matrix).length > 0 && (
                <div data-testid="date-matrix">
                  <p className="text-sm font-medium mb-2">Classification x issue date (month)</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-1.5">Class</th>
                          {(() => {
                            const allMonths = new Set();
                            Object.values(result.date_matrix).forEach((m) =>
                              Object.keys(m).forEach((k) => allMonths.add(k))
                            );
                            return [...allMonths].sort().map((m) => (
                              <th key={m} className="text-right p-1.5 whitespace-nowrap">{m}</th>
                            ));
                          })()}
                        </tr>
                      </thead>
                      <tbody>
                        {['bros_native_pdf', 'external_pdf', 'no_pdf', 'other']
                          .filter((c) => result.date_matrix[c])
                          .map((c) => {
                            const allMonths = new Set();
                            Object.values(result.date_matrix).forEach((m) =>
                              Object.keys(m).forEach((k) => allMonths.add(k))
                            );
                            const sorted = [...allMonths].sort();
                            return (
                              <tr key={c} className="border-t">
                                <td className={`p-1.5 font-mono ${CLASS_TEXT[c] || ''}`}>{CLASS_LABELS[c]}</td>
                                {sorted.map((m) => (
                                  <td key={m} className="p-1.5 text-right font-mono">
                                    {result.date_matrix[c]?.[m] || ''}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* BR OS renderability summary */}
              {result.bros_renderability && (
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="text-muted-foreground">BR OS renderability:</span>
                  {result.bros_renderability.checked ? (
                    <>
                      <Badge variant="outline" className="text-[10px]">
                        {result.bros_renderability.renderable_count} renderable
                      </Badge>
                      <Badge variant="outline" className={`text-[10px] ${result.bros_renderability.not_renderable_count > 0 ? 'border-red-300 text-red-600' : ''}`}>
                        {result.bros_renderability.not_renderable_count} not renderable
                      </Badge>
                    </>
                  ) : (
                    <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">
                      check unavailable
                    </Badge>
                  )}
                </div>
              )}

              {/* Provenance fields audit */}
              {result.provenance_fields_audit && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    Provenance fields checked on bros_invoices documents (expected: none present)
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(result.provenance_fields_audit).map(([field, info]) => (
                      <span
                        key={field}
                        className={`text-[10px] font-mono border rounded px-1.5 py-0.5 ${
                          info.non_empty_count > 0
                            ? 'border-amber-300 text-amber-700 dark:text-amber-400'
                            : 'border-muted text-muted-foreground'
                        }`}
                      >
                        {field}: {info.non_empty_count}/{info.present_count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Samples per class */}
              {result.samples && Object.keys(result.samples).length > 0 && (
                <div data-testid="samples">
                  <p className="text-sm font-medium mb-2">Sample rows per class (up to 10)</p>
                  {['bros_native_pdf', 'external_pdf', 'no_pdf', 'other']
                    .filter((c) => result.samples[c]?.length > 0)
                    .map((c) => (
                      <div key={c} className="mb-3">
                        <p className={`text-xs font-medium mb-1 ${CLASS_TEXT[c] || ''}`}>
                          {CLASS_LABELS[c]} ({result.samples[c].length})
                        </p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-[10px]">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="text-left p-1">bros_id</th>
                                <th className="text-left p-1">status</th>
                                <th className="text-right p-1">amount</th>
                                <th className="text-left p-1">currency</th>
                                <th className="text-left p-1">issue_date</th>
                                <th className="text-left p-1">company</th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.samples[c].map((row, i) => (
                                <tr key={i} className="border-t">
                                  <td className="p-1 font-mono">{row.bros_id}</td>
                                  <td className="p-1">{row.status}</td>
                                  <td className="p-1 text-right font-mono">{row.amount}</td>
                                  <td className="p-1">{row.currency}</td>
                                  <td className="p-1 font-mono">{row.issue_date}</td>
                                  <td className="p-1 truncate max-w-[120px]">{row.company}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* No PDF warning */}
              {(cls?.no_pdf ?? 0) > 0 && (
                <div className="border rounded-lg p-3 border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/10">
                  <p className="text-xs font-medium text-red-600">
                    {cls.no_pdf} invoice{cls.no_pdf !== 1 ? 's' : ''} with no PDF artifact found anywhere.
                    {result.status_matrix?.no_pdf?.paid > 0 && (
                      <> {result.status_matrix.no_pdf.paid} of these are paid, meaning money collected with no backing document.</>
                    )}
                  </p>
                </div>
              )}

              {/* Errors */}
              {result.errors?.length > 0 && (
                <div className="border rounded-lg p-2 border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/10">
                  <p className="text-xs font-medium text-red-600 mb-1">Errors</p>
                  {result.errors.map((e, i) => (
                    <p key={i} className="text-[10px] text-red-600/80 font-mono">{e}</p>
                  ))}
                </div>
              )}

              <p className="text-[10px] text-muted-foreground text-right">
                Generated {result.generated_at} | {result.build_commit}
              </p>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}
